/**
 * Authentication API functions
 */

import { apiClient } from '../api-client';
import type { LoginRequest, RegisterRequest, TokenResponse, User } from './types';

export const authApi = {
  /**
   * Login user
   */
  login: async (credentials: LoginRequest): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/login', credentials);
    
    // Store tokens and user info
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    return response;
  },  

  /**
   * create Cometchat User
   */
  createCometchatUser: async (uid: string, name: string): Promise<void> => {
    await apiClient.post('/auth/create-cometchat-user', {
      uid: uid,
      name: name,
    });
  },

  /**
   * Register new user
   * Note: Does NOT auto-login the registered user to prevent session hijacking
   * when coordinators/PAs register patients
   */
  register: async (userData: RegisterRequest): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/register', userData);
    
    // DO NOT store tokens and user info here
    // This prevents auto-login when a coordinator/PA registers a patient
    // If self-registration is needed, use registerAndLogin instead
    
    return response;
  },

  /**
   * Register new user and auto-login (for self-registration flows)
   */
  registerAndLogin: async (userData: RegisterRequest): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/register', userData);
    
    // Store tokens and user info for auto-login
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    return response;
  },

  /**
   * Get current user info
   */
  getCurrentUser: async (): Promise<User> => {
    return apiClient.get<User>('/auth/me');
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Change password
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /**
   * Update user profile
   */
  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    date_of_birth?: string;
    address?: string;
    emergency_contact?: string;
  }): Promise<User> => {
    const response = await apiClient.put<User>('/auth/profile', data);
    
    // Update stored user
    localStorage.setItem('user', JSON.stringify(response));
    
    return response;
  },

  /**
   * Update user preferences
   */
  updatePreferences: async (preferences: {
    language?: string;
    exercise_reminders?: boolean;
    appointment_alerts?: boolean;
    medication_reminders?: boolean;
    email_notifications?: boolean;
  }): Promise<{ message: string; preferences: any }> => {
    return apiClient.put('/auth/preferences', preferences);
  },

  /**
   * Update user profile (Enhanced version matching backend endpoint)
   * Used by: ProfileSettings component
   */
  updateUserProfile: async (data: {
    full_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    emergency_contact?: string;
  }): Promise<{
    status: string;
    message: string;
    user: {
      id: number;
      full_name: string;
      email: string;
      phone: string;
    };
  }> => {
    return apiClient.put('/auth/users/profile', data);
  },

  /**
   * Update user notification preferences (Enhanced version)
   * Used by: ProfileSettings component
   */
  updateUserPreferences: async (data: {
    notifications_enabled: boolean;
    sms_enabled: boolean;
    email_enabled: boolean;
    push_enabled: boolean;
  }): Promise<{
    status: string;
    message: string;
    preferences: {
      notifications_enabled: boolean;
      sms_enabled: boolean;
      email_enabled: boolean;
      push_enabled: boolean;
    };
  }> => {
    return apiClient.put('/auth/users/preferences', data);
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email: string): Promise<{ message: string; reset_code?: string }> => {
    return apiClient.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with code
   */
  resetPassword: async (email: string, resetCode: string, newPassword: string): Promise<{ message: string }> => {
    return apiClient.post('/auth/reset-password', {
      email,
      reset_code: resetCode,
      new_password: newPassword,
    });
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Get stored user
   */
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};